const STRUCTURED_MARKER = '<<<STRUCTURED_JSON>>>';
const USER_MARKER = '<<<USER_REPLY>>>';
const USER_MARKER_RE = /<{2,3}\s*USER_REPLY\s*>{2,3}/i;
const STRUCTURED_MARKER_RE = /<{2,3}\s*STRUCTURED_JSON\s*>{2,3}/i;

/** 从模型回复中解析项目信息局部改写的 JSON（纯 JSON 或 <<<STRUCTURED_JSON>>> 段） */
export function parseProjectCreateFieldEditResponse(answer: string): { original: string; revised: string } | null {
  const raw = String(answer ?? '').trim();
  if (!raw) return null;

  const tryParse = (s: string): { original?: string; revised?: string } | null => {
    try {
      const o = JSON.parse(s) as Record<string, unknown>;
      const original = o.original ?? o.待修改原文 ?? o.source;
      const revised = o.revised ?? o.改写结果 ?? o.target ?? o.replacement;
      if (typeof original === 'string' && typeof revised === 'string') {
        return { original, revised };
      }
      return null;
    } catch {
      return null;
    }
  };

  const fence = raw.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence) {
    const p = tryParse(fence[1].trim());
    if (p?.original != null && p.revised != null) {
      return { original: String(p.original).trim(), revised: String(p.revised).trim() };
    }
  }

  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start >= 0 && end > start) {
    const p = tryParse(raw.slice(start, end + 1));
    if (p?.original != null && p.revised != null) {
      return { original: String(p.original).trim(), revised: String(p.revised).trim() };
    }
  }

  return null;
}

/**
 * 解析「自然语言 + 结构化 JSON」双段；若无分段则整段按 JSON 解析。
 */
function collectThoughtCandidateTexts(thoughtRaw: unknown): string[] {
  const texts: string[] = [];
  if (thoughtRaw == null) return texts;
  try {
    const parsed =
      typeof thoughtRaw === 'string'
        ? (JSON.parse(thoughtRaw) as Record<string, unknown>)
        : (thoughtRaw as Record<string, unknown>);
    if (!parsed || typeof parsed !== 'object') return texts;
    for (const key of ['analysis_result', 'solution_plan', 'final_answer']) {
      const v = parsed[key];
      if (v != null && String(v).trim()) texts.push(String(v).trim());
    }
    const steps = Array.isArray(parsed.steps) ? parsed.steps : [];
    for (const step of steps) {
      if (!step || typeof step !== 'object') continue;
      const o = step as Record<string, unknown>;
      const t = o.text ?? o.content ?? o.message;
      if (t != null && String(t).trim()) texts.push(String(t).trim());
    }
  } catch {
    /* ignore */
  }
  return texts;
}

function normalizeQuoteMatchText(text: string): string {
  return String(text ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/[\r\n\u2028\u2029]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function fieldEditMatchesQuote(
  edit: { original: string; revised: string } | null,
  quoted?: string,
): boolean {
  if (!edit) return false;
  const q = String(quoted ?? '').trim();
  if (!q) return true;
  const o = String(edit.original ?? '').trim();
  if (!o) return false;
  const qNorm = normalizeQuoteMatchText(q);
  const oNorm = normalizeQuoteMatchText(o);
  return o === q || o.includes(q) || q.includes(o) || oNorm === qNorm || oNorm.includes(qNorm) || qNorm.includes(oNorm);
}

const FIELD_EDIT_PLANNING_REPLY_RE =
  /^(?:用户希望|我会(?:修正|调整|优化|改写|整合|统一)|我将(?:修正|调整|优化|改写|整合|统一)|待修改原文)/;

/** 面向用户展示：去掉 USER_REPLY / STRUCTURED_JSON 等协议标记与 JSON 段 */
export function sanitizeFieldEditUserDisplayText(text: string): string {
  let raw = String(text ?? '').trim();
  if (!raw) return '';
  const structMatch = raw.match(STRUCTURED_MARKER_RE);
  if (structMatch?.index != null && structMatch.index >= 0) {
    raw = raw.slice(0, structMatch.index).trim();
  }
  const userMatch = raw.match(USER_MARKER_RE);
  if (userMatch?.index != null && userMatch.index >= 0) {
    raw = raw.slice(userMatch.index + userMatch[0].length).trim();
  }
  raw = raw.replace(USER_MARKER_RE, '').replace(STRUCTURED_MARKER_RE, '').trim();
  if (raw.startsWith('{') && raw.includes('"original"')) return '';
  if (FIELD_EDIT_PLANNING_REPLY_RE.test(raw)) return '';
  return raw;
}

/** 从可能混入 PROJECT 风险分析正文的字符串里，只保留 <<<USER_REPLY>>> 段。 */
function extractUserReplySegment(text: string): string {
  return sanitizeFieldEditUserDisplayText(text);
}

function sanitizeFieldEditDualAnswer(parsed: {
  userText: string;
  edit: { original: string; revised: string } | null;
}): { userText: string; edit: { original: string; revised: string } | null } {
  return {
    userText: extractUserReplySegment(parsed.userText),
    edit: parsed.edit,
  };
}

/**
 * 优先从 final answer 解析；若为空或与引用原文不匹配（常见于误走 PROJECT 流水线导致 answer 被污染），
 * 则回退到 thought（深度分析/解决方案等步骤中的双段输出）。
 */
export function resolveFieldEditDualAnswer(
  answer: string,
  thoughtRaw: unknown,
  quoted?: string,
): {
  userText: string;
  edit: { original: string; revised: string } | null;
} {
  const q = String(quoted ?? '').trim();
  const fromAnswer = sanitizeFieldEditDualAnswer(parseProjectCreateFieldEditDualAnswer(answer));

  const candidates = collectThoughtCandidateTexts(thoughtRaw);
  const prioritized = [
    ...candidates.filter((t) => t.includes('<<<STRUCTURED_JSON>>>') || t.includes('"original"')),
    ...candidates.filter((t) => !t.includes('<<<STRUCTURED_JSON>>>') && !t.includes('"original"')),
  ];

  const answerUsable = fromAnswer.edit && fieldEditMatchesQuote(fromAnswer.edit, q);
  const sources = q
    ? [...prioritized, ...(answerUsable ? [answer] : [])]
    : [answer, ...prioritized];

  for (const text of sources) {
    const parsed = sanitizeFieldEditDualAnswer(parseProjectCreateFieldEditDualAnswer(text));
    if (!parsed.edit) continue;
    if (q && !fieldEditMatchesQuote(parsed.edit, q)) continue;
    return parsed;
  }

  if (q) {
    const combined = [answer, ...candidates].filter(Boolean).join('\n\n');
    const inferred = tryInferFieldEditFromProse(combined, q);
    if (inferred) {
      return { userText: fromAnswer.userText, edit: inferred };
    }
  }
  return fromAnswer;
}

export function parseProjectCreateFieldEditDualAnswer(answer: string): {
  userText: string;
  edit: { original: string; revised: string } | null;
} {
  const raw = String(answer ?? '').trim();
  if (!raw) return { userText: '', edit: null };

  let userPart = '';
  let jsonBlob = '';

  const structMatch = raw.match(STRUCTURED_MARKER_RE);
  if (structMatch?.index != null && structMatch.index >= 0) {
    userPart = raw.slice(0, structMatch.index).trim();
    jsonBlob = raw.slice(structMatch.index + structMatch[0].length).trim();
  } else if (USER_MARKER_RE.test(raw)) {
    userPart = raw;
    jsonBlob = '';
  } else {
    jsonBlob = raw;
  }

  userPart = extractUserReplySegment(userPart || (USER_MARKER_RE.test(raw) ? raw : ''));
  const edit = parseProjectCreateFieldEditResponse(jsonBlob || raw);
  return { userText: userPart, edit };
}

/**
 * 当模型未按约定输出 JSON 时，从中文说明里尽量提取「改写为 …」的目标文案（与 pending 原文配对）。
 */
export function tryInferFieldEditFromProse(answer: string, quoted: string): { original: string; revised: string } | null {
  const q = String(quoted ?? '').trim();
  if (!q) return null;
  const a = String(answer ?? '');
  const patterns: RegExp[] = [
    /改写为[「"\u201c](.+?)[」"\u201d]/,
    /改为[「"\u201c](.+?)[」"\u201d]/,
    /结果是[「"\u201c](.+?)[」"\u201d]/,
    /更新为[「"\u201c](.+?)[」"\u201d]/,
    /调整为[「"\u201c](.+?)[」"\u201d]/,
  ];
  for (const re of patterns) {
    const m = a.match(re);
    if (m?.[1]) {
      const revised = m[1].trim();
      if (revised && revised !== q) {
        return { original: q, revised };
      }
    }
  }
  return null;
}

export function buildProjectCreateFieldEditPrompt(
  quoted: string,
  fieldLabel: string,
  userRequirement: string,
  options?: { fieldPath?: string; positionDescription?: string },
): string {
  const q = String(quoted ?? '').trim();
  const req = String(userRequirement ?? '').trim();
  const pos = String(options?.positionDescription ?? '').trim();
  const fp = String(options?.fieldPath ?? '').trim();
  const where = pos || `创建项目页左栏「项目信息」·「${fieldLabel}」表单项中的选中片段`;
  return [
    '【系统任务：项目创建页-信息片段改写】',
    '用户正在「创建项目」页的「项目信息」表单中，选中了某一具体位置的文本片段，并说明要如何修改。',
    '你必须严格按下述两段输出（不要交换顺序、不要用 Markdown 代码块包裹 JSON 段）：',
    '',
    USER_MARKER,
    '用 1～4 句中文 Markdown，**直接对用户**说明本次改写思路与改进点（使用「您/你」，禁止写「用户希望…我会…」等第三人称计划或复述待修改原文）。不要在此段重复 JSON。',
    '',
    STRUCTURED_MARKER,
    '仅一个 JSON 对象，键名固定为：',
    '{"original":"<待修改原文，必须与下方「待修改原文」逐字一致>","revised":"<按用户要求改写后的完整替换文本>"}',
    '注意：JSON 里的 original 必须与「待修改原文」完全一致（含空格与标点）。',
    '【待修改位置】',
    where,
    fp ? `【字段路径】（机器可读，勿写入 JSON 的 original/revised 内容）：${fp}` : '',
    `【字段短名】${fieldLabel}`,
    '',
    '【待修改原文】',
    q,
    '',
    '【用户修改要求】',
    req || '（未额外说明，请根据上下文合理润色或微调该片段）',
  ]
    .filter((line) => line !== '')
    .join('\n');
}
